import { useState, useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, CartesianGrid, Legend } from "recharts";

const C = {
  bg: "#F5F6FA", surface: "#FFFFFF", card: "#FFFFFF", cardBorder: "#E2E5EC",
  hover: "#F0F1F5", accent: "#D23A3B", accentSoft: "rgba(210,58,59,0.08)",
  green: "#16A34A", greenSoft: "rgba(22,163,74,0.08)",
  red: "#DC2626", redSoft: "rgba(220,38,38,0.08)",
  orange: "#D97706", orangeSoft: "rgba(217,119,6,0.08)",
  purple: "#D23A3B", purpleSoft: "rgba(210,58,59,0.08)",
  t1: "#1A1D2E", t2: "#5A6178", t3: "#9CA3B8", divider: "#E8EAF0",
  shadow: "0 1px 3px rgba(0,0,0,0.06)",
};

// Material Symbols Rounded helper
const Icon = ({ name, size = 18, color, style = {} }: any) => (
  <span
    className="material-symbols-rounded"
    style={{ fontSize: size, lineHeight: 1, color: color || "inherit", userSelect: "none", flexShrink: 0, ...style }}
  >
    {name}
  </span>
);

const Phone = ({ children, label }: any) => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
    <div style={{
      width: 375, height: 812, background: C.bg, borderRadius: 40, overflow: "hidden",
      border: "3px solid #D1D5DB", position: "relative", boxShadow: "0 20px 60px rgba(0,0,0,0.12)",
      display: "flex", flexDirection: "column",
    }}>
      <div style={{ height: 44, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 28px", flexShrink: 0 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: C.t1 }}>9:41</span>
        <div style={{ width: 125, height: 30, background: C.bg, borderRadius: "0 0 16px 16px", position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)" }} />
        <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
          <span style={{ fontSize: 11, color: C.t2 }}>5G</span>
          <Icon name="battery_full" size={16} color={C.t2} />
        </div>
      </div>
      {children}
    </div>
    <span style={{ fontSize: 13, fontWeight: 600, color: "#5A6178", letterSpacing: 0.5 }}>{label}</span>
  </div>
);

const NAV = [
  { icon: "flash_on",     label: "Snapshot"  },
  { icon: "bar_chart",    label: "Reports"   },
];

const BottomNav = ({ active }: any) => (
  <div style={{ height: 72, background: C.surface, borderTop: `1px solid ${C.divider}`, display: "flex", justifyContent: "space-around", alignItems: "center", flexShrink: 0, paddingBottom: 8 }}>
    {NAV.map((item, i) => (
      <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, padding: "4px 14px" }}>
        <Icon name={item.icon} size={22} color={active === i ? C.accent : C.t3} style={{ opacity: active === i ? 1 : 0.45 }} />
        <span style={{ fontSize: 9.5, fontWeight: active === i ? 700 : 400, color: active === i ? C.accent : C.t3 }}>{item.label}</span>
        {active === i && <div style={{ width: 4, height: 4, borderRadius: "50%", background: C.accent, boxShadow: `0 0 8px ${C.accent}44`, marginTop: -1 }} />}
      </div>
    ))}
  </div>
);

const Arrow = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" style={{ position: "absolute", top: 6, right: 6, opacity: 0.45 }}>
    <path d="M3.5 2L7 5L3.5 8" fill="none" stroke={C.accent} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DatePicker = ({ compact }: any) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: compact ? 8 : 12, marginBottom: compact ? 10 : 0 }}>
    <div style={{ width: 26, height: 26, borderRadius: 7, background: C.hover, border: `1px solid ${C.divider}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
      <Icon name="chevron_left" size={17} color={C.t2} />
    </div>
    <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 12px", borderRadius: 8, background: C.surface, border: `1px solid ${C.divider}`, cursor: "pointer" }}>
      <Icon name="calendar_today" size={13} color={C.t3} />
      <span style={{ fontSize: 12, fontWeight: 600, color: C.t1 }}>Today</span>
      <span style={{ fontSize: 11, color: C.t3 }}>Mar 2, 2026</span>
    </div>
    <div style={{ width: 26, height: 26, borderRadius: 7, background: C.hover, border: `1px solid ${C.divider}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", opacity: 0.3 }}>
      <Icon name="chevron_right" size={17} color={C.t3} />
    </div>
  </div>
);

const OutletDropdown = () => (
  <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 10px", borderRadius: 8, background: C.surface, border: `1px solid ${C.divider}`, cursor: "pointer" }}>
    <Icon name="location_on" size={14} color={C.accent} />
    <span style={{ fontSize: 11, fontWeight: 600, color: C.t1 }}>Downtown Chicago</span>
    <Icon name="expand_more" size={14} color={C.t3} />
  </div>
);

const DrillHeader = ({ title, sub }: any) => (
  <div style={{ padding: "8px 20px 6px", flexShrink: 0 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ width: 28, height: 28, borderRadius: 8, background: C.hover, border: `1px solid ${C.divider}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Icon name="chevron_left" size={18} color={C.t2} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 11, color: C.t3 }}>Snapshot ›</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: C.t1 }}>{title}</div>
      </div>
    </div>
    {sub && <div style={{ fontSize: 10, color: C.t3, marginLeft: 36, marginTop: 1 }}>{sub}</div>}
    <div style={{ marginTop: 8 }}><DatePicker compact /></div>
  </div>
);

const KPI = ({ label, value, delta, up, drill }: any) => (
  <div style={{ background: C.card, border: `1px solid ${drill ? C.accent + "33" : C.cardBorder}`, borderRadius: 12, padding: "12px 10px", textAlign: "center", cursor: drill ? "pointer" : "default", position: "relative", boxShadow: C.shadow }}>
    {drill && <Arrow />}
    <div style={{ fontSize: 8, color: C.t3, textTransform: "uppercase", letterSpacing: 0.3 }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 700, color: C.t1, marginTop: 4 }}>{value}</div>
    {delta && <div style={{ fontSize: 9, fontWeight: 600, color: up ? C.green : C.red, marginTop: 3 }}>{up ? "↑" : "↓"} {delta}</div>}
  </div>
);

const StatKPI = ({ label, value, drill }: any) => (
  <div style={{ background: C.card, border: `1px solid ${drill ? C.accent + "33" : C.cardBorder}`, borderRadius: 10, padding: "10px 10px", textAlign: "center", cursor: drill ? "pointer" : "default", position: "relative", boxShadow: C.shadow }}>
    {drill && <Arrow />}
    <div style={{ fontSize: 8, color: C.t3, textTransform: "uppercase", letterSpacing: 0.3 }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 700, color: C.t1, marginTop: 3 }}>{value}</div>
  </div>
);

const VertBarChart = ({ data, labels, color, prefix, max: maxOverride }: any) => {
  const max = maxOverride || Math.max(...data);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 100, paddingBottom: 20, position: "relative" }}>
      <div style={{ position: "absolute", left: 0, top: 0, bottom: 20, display: "flex", flexDirection: "column", justifyContent: "space-between", width: 28 }}>
        <span style={{ fontSize: 8, color: C.t3 }}>{prefix}{max}</span>
        <span style={{ fontSize: 8, color: C.t3 }}>{prefix}{Math.round(max / 2)}</span>
        <span style={{ fontSize: 8, color: C.t3 }}>{prefix}0</span>
      </div>
      <div style={{ position: "absolute", left: 30, right: 0, top: 0, bottom: 20 }}>
        {[0, 0.5, 1].map((t, i) => (
          <div key={i} style={{ position: "absolute", left: 0, right: 0, top: `${t * 100}%`, height: 1, background: C.divider, opacity: 0.5 }} />
        ))}
      </div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 2, flex: 1, marginLeft: 30, height: 80 }}>
        {data.map((d, i) => {
          const isPeak = d === Math.max(...data);
          return (
            <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
              <div style={{ width: "100%", maxWidth: 18, height: `${(d / max) * 100}%`, minHeight: 2, borderRadius: "3px 3px 0 0", background: isPeak ? color : `${color}66` }} />
              <span style={{ fontSize: 7, color: isPeak ? C.t1 : C.t3, fontWeight: isPeak ? 700 : 400 }}>{labels[i]}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// =========== SNAPSHOT ===========
const SnapshotScreen = () => (
  <Phone label="SNAPSHOT">
    <div style={{ padding: "8px 20px 0", flexShrink: 0 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: C.t1 }}>Snapshot</div>
        <div style={{ width: 30, height: 30, borderRadius: 8, background: `linear-gradient(135deg, ${C.accent}, #E86B6B)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: "#fff" }}>JD</div>
      </div>
      <div style={{ marginTop: 6 }}><OutletDropdown /></div>
      <div style={{ marginTop: 8 }}><DatePicker /></div>
    </div>
    <div style={{ flex: 1, overflowY: "auto", padding: "8px 16px 16px" }}>
      <div style={{ background: `linear-gradient(135deg, ${C.accent}0D, ${C.purple}0A)`, border: `1px solid ${C.accent}22`, borderRadius: 16, padding: "16px 18px", marginBottom: 10, position: "relative", overflow: "hidden", cursor: "pointer", boxShadow: C.shadow }}>
        <Arrow />
        <div style={{ position: "absolute", top: -30, right: -30, width: 100, height: 100, borderRadius: "50%", background: C.accentSoft, filter: "blur(30px)" }} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: 10, color: C.t2, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Net Sales</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <span style={{ fontSize: 34, fontWeight: 700, color: C.t1, letterSpacing: -1 }}>$5,928</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: C.green }}>↑ 11.8%</span>
            </div>
            <div style={{ fontSize: 10, color: C.t3, marginTop: 3 }}>vs yesterday $5,302</div>
          </div>
          <div style={{ textAlign: "right", marginTop: 4 }}>
            <div style={{ marginBottom: 6, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 6 }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 -960 960 960" fill={C.t3}>
                <path d="M320-200h60v-270q26-8 43-28.5t17-49.5v-152q0-8-6-14t-14-6q-8 0-14 6t-6 14v100h-30v-100q0-8-6-14t-14-6q-8 0-14 6t-6 14v100h-30v-100q0-8-6-14t-14-6q-8 0-14 6t-6 14v152q0 29 17 49.5t43 28.5v270Zm240 0h60v-254q33-16 51.5-51t18.5-82q0-57-28.5-95T590-720q-43 0-71.5 38T490-587q0 47 18.5 82t51.5 51v254ZM160-80q-33 0-56.5-23.5T80-160v-640q0-33 23.5-56.5T160-880h640q33 0 56.5 23.5T880-800v640q0 33-23.5 56.5T800-80H160Zm0-80h640v-640H160v640Zm0 0v-640 640Z"/>
              </svg>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.t1 }}>$2,608</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 6 }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 -960 960 960" fill={C.t3}>
                <path d="M120-640v-200h280v200H120Zm80-80h120v-40H200v40Zm-5 485q-35-35-35-85H80v-120q0-66 47-113t113-47h160v200h140l140-174v-106H560v-80h120q33 0 56.5 23.5T760-680v134L580-320H400q0 50-35 85t-85 35q-50 0-85-35Zm113.5-56.5Q320-303 320-320h-80q0 17 11.5 28.5T280-280q17 0 28.5-11.5ZM675-235q-35-35-35-85t35-85q35-35 85-35t85 35q35 35 35 85t-35 85q-35 35-85 35t-85-35Zm113.5-56.5Q800-303 800-320t-11.5-28.5Q777-360 760-360t-28.5 11.5Q720-337 720-320t11.5 28.5Q743-280 760-280t28.5-11.5ZM160-400h160v-120h-80q-33 0-56.5 23.5T160-440v40Zm160-320v-40 40Zm0 320Z"/>
              </svg>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.t1 }}>$1,842</div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 8 }}>
        <KPI label="Orders" value="287" delta="+8.3%" up={true} drill={true} />
        <KPI label="Avg Order" value="$23.85" delta="-2.1%" up={false} drill={false} />
        <KPI label="Cancelled" value="$142" delta="+22%" up={false} drill={true} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 8 }}>
        <StatKPI label="Active Staff" value="6" drill={true} />
        <StatKPI label="Sales/Hr" value="$392" drill={false} />
        <StatKPI label="Voids" value="3" drill={true} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 8 }}>
        <StatKPI label="Tips" value="$847" drill={true} />
        <StatKPI label="Taxes" value="$456" drill={false} />
        <StatKPI label="Discounts" value="$320" drill={false} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 10 }}>
        <StatKPI label="Check-Ins" value="84" drill={false} />
        <StatKPI label="Avg Wait" value="12m" drill={false} />
        <StatKPI label="Guests" value="218" drill={false} />
      </div>
      <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: "12px 14px", boxShadow: C.shadow }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: C.t1, marginBottom: 8 }}>Top 3 Items Today</div>
        {[{ name: "Classic Burger", qty: 42, rev: "$630" }, { name: "Margherita Pizza", qty: 38, rev: "$570" }, { name: "Chicken Wings", qty: 31, rev: "$465" }].map((item, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "5px 0", borderBottom: i < 2 ? `1px solid ${C.divider}` : "none" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 18, height: 18, borderRadius: 5, background: i === 0 ? C.accentSoft : C.hover, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700, color: i === 0 ? C.accent : C.t3 }}>{i + 1}</span>
              <span style={{ fontSize: 11, color: C.t1 }}>{item.name}</span>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <span style={{ fontSize: 10, color: C.t3 }}>{item.qty} qty</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: C.t1 }}>{item.rev}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
    <BottomNav active={0} />
  </Phone>
);

// =========== DRILL 1: NET SALES ===========
const DrillNetSales = () => {
  const hours = ["6a","7a","8a","9a","10a","11a","12p","1p","2p","3p","4p","5p","6p","7p","8p","9p"];
  const data = [82,145,210,380,520,680,890,760,540,420,480,620,840,920,780,460];
  return (
    <Phone label="DRILL: NET SALES">
      <DrillHeader title="Net Sales" sub="$5,928 total" />
      <div style={{ flex: 1, overflowY: "auto", padding: "0 16px 16px" }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          {[{ l: "Net Sales", v: "$5,928" }, { l: "Peak Hour", v: "7 PM" }, { l: "Avg/Hour", v: "$494" }].map((k, i) => (
            <div key={i} style={{ flex: 1, background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 10, padding: "8px 6px", textAlign: "center", boxShadow: C.shadow }}>
              <div style={{ fontSize: 7, color: C.t3, textTransform: "uppercase" }}>{k.l}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: i === 1 ? C.accent : C.t1, marginTop: 2 }}>{k.v}</div>
            </div>
          ))}
        </div>
        <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 14, padding: "14px 12px", marginBottom: 12, boxShadow: C.shadow }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 8 }}>Hourly Revenue</div>
          <VertBarChart data={data} labels={hours} color={C.accent} prefix="$" />
        </div>
        <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 14, padding: 14, boxShadow: C.shadow }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 10 }}>By Staff</div>
          {[{ n: "Sarah Chen", s: "$1,420", p: 24 }, { n: "Mike Rodriguez", s: "$1,185", p: 20 }, { n: "Emily Park", s: "$1,068", p: 18 }, { n: "James Wilson", s: "$890", p: 15 }, { n: "Aisha Patel", s: "$742", p: 13 }, { n: "David Kim", s: "$623", p: 10 }].map((s, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
              <span style={{ width: 14, fontSize: 9, color: C.t3, textAlign: "right" }}>{i + 1}</span>
              <span style={{ flex: 1, fontSize: 11, color: C.t1 }}>{s.n}</span>
              <span style={{ fontSize: 10, color: C.t3, width: 28 }}>{s.p}%</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: C.t1, width: 48, textAlign: "right" }}>{s.s}</span>
            </div>
          ))}
        </div>
      </div>
      <BottomNav active={0} />
    </Phone>
  );
};

// =========== DRILL 2: ORDERS ===========
const DrillOrders = () => {
  const hours = ["6a","7a","8a","9a","10a","11a","12p","1p","2p","3p","4p","5p","6p","7p","8p","9p"];
  const data = [4,8,12,18,24,32,42,36,26,20,22,28,38,44,36,22];
  return (
    <Phone label="DRILL: ORDERS">
      <DrillHeader title="Orders" sub="287 total" />
      <div style={{ flex: 1, overflowY: "auto", padding: "0 16px 16px" }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          {[{ l: "Total", v: "287" }, { l: "Peak Hour", v: "7 PM" }, { l: "Avg/Hour", v: "18" }].map((k, i) => (
            <div key={i} style={{ flex: 1, background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 10, padding: "8px 6px", textAlign: "center", boxShadow: C.shadow }}>
              <div style={{ fontSize: 7, color: C.t3, textTransform: "uppercase" }}>{k.l}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: i === 1 ? C.accent : C.t1, marginTop: 2 }}>{k.v}</div>
            </div>
          ))}
        </div>
        <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 14, padding: "14px 12px", marginBottom: 12, boxShadow: C.shadow }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 8 }}>Hourly Orders</div>
          <VertBarChart data={data} labels={hours} color={C.green} prefix="" />
        </div>
        <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 14, padding: 14, boxShadow: C.shadow }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 10 }}>By Channel</div>
          {[{ c: "Dine-In", n: 124, p: 43 }, { c: "Delivery (Maghil)", n: 68, p: 24 }, { c: "DoorDash", n: 42, p: 15 }, { c: "Uber Eats", n: 31, p: 11 }, { c: "In-Store Pickup", n: 22, p: 7 }].map((ch, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 0", borderBottom: i < 4 ? `1px solid ${C.divider}` : "none" }}>
              <span style={{ fontSize: 12, color: C.t1 }}>{ch.c}</span>
              <div style={{ display: "flex", gap: 10 }}>
                <span style={{ fontSize: 10, color: C.t3 }}>{ch.p}%</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: C.t1, width: 28, textAlign: "right" }}>{ch.n}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      <BottomNav active={0} />
    </Phone>
  );
};

// =========== DRILL 3: CANCELLED ===========
const DrillCancelled = () => (
  <Phone label="DRILL: CANCELLED">
    <DrillHeader title="Cancelled Orders" sub="7 orders · $142 total" />
    <div style={{ flex: 1, overflowY: "auto", padding: "0 16px 16px" }}>
      <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 14, padding: 14, marginBottom: 12, boxShadow: C.shadow }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 10 }}>Cancel Reasons</div>
        {[{ r: "Customer changed mind", c: 3, p: 43 }, { r: "Long wait time", c: 2, p: 28 }, { r: "Item unavailable", c: 1, p: 14 }, { r: "Duplicate order", c: 1, p: 14 }].map((r, i) => (
          <div key={i} style={{ marginBottom: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
              <span style={{ fontSize: 11, color: C.t2 }}>{r.r}</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: C.t1 }}>{r.c}x</span>
            </div>
            <div style={{ height: 5, background: C.hover, borderRadius: 3, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${r.p}%`, background: C.red, borderRadius: 3, opacity: 0.6 }} />
            </div>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 8 }}>Order Log</div>
      {[
        { id: "#4821", time: "2:34 PM", amt: "$28.50", reason: "Customer changed mind", staff: "Mike Rodriguez", ch: "Dine-In" },
        { id: "#4798", time: "1:12 PM", amt: "$22.00", reason: "Long wait time", staff: "James Wilson", ch: "Dine-In" },
        { id: "#4776", time: "12:45 PM", amt: "$18.75", reason: "Customer changed mind", staff: "Emily Park", ch: "Delivery" },
        { id: "#4752", time: "11:30 AM", amt: "$24.00", reason: "Item unavailable", staff: "Sarah Chen", ch: "DoorDash" },
        { id: "#4731", time: "10:58 AM", amt: "$16.50", reason: "Long wait time", staff: "Aisha Patel", ch: "Dine-In" },
        { id: "#4718", time: "10:15 AM", amt: "$19.25", reason: "Customer changed mind", staff: "Mike Rodriguez", ch: "Uber Eats" },
        { id: "#4695", time: "9:20 AM", amt: "$13.00", reason: "Duplicate order", staff: "System", ch: "DoorDash" },
      ].map((o, i) => (
        <div key={i} style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 10, padding: "10px 12px", marginBottom: 5, boxShadow: C.shadow }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: C.t1 }}>{o.id}</span>
              <span style={{ fontSize: 10, color: C.t3 }}>{o.time}</span>
            </div>
            <span style={{ fontSize: 12, fontWeight: 600, color: C.red }}>{o.amt}</span>
          </div>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.accent, marginBottom: 2 }}>{o.staff}</div>
          <div style={{ fontSize: 10, color: C.t2, marginBottom: 3 }}>{o.reason}</div>
          <span style={{ fontSize: 9, color: C.t3, padding: "2px 6px", borderRadius: 4, background: C.hover }}>{o.ch}</span>
        </div>
      ))}
    </div>
    <BottomNav active={0} />
  </Phone>
);

// =========== DRILL 4: ACTIVE STAFF ===========
const DrillStaff = () => (
  <Phone label="DRILL: ACTIVE STAFF">
    <DrillHeader title="Active Staff" sub="6 active · 2 on break" />
    <div style={{ flex: 1, overflowY: "auto", padding: "0 16px 16px" }}>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        {[{ l: "Active", v: "6", c: C.green }, { l: "On Break", v: "2", c: C.orange }, { l: "Total Hrs", v: "52h", c: C.t1 }].map((k, i) => (
          <div key={i} style={{ flex: 1, background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 10, padding: "8px 6px", textAlign: "center", boxShadow: C.shadow }}>
            <div style={{ fontSize: 7, color: C.t3, textTransform: "uppercase" }}>{k.l}</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: k.c, marginTop: 2 }}>{k.v}</div>
          </div>
        ))}
      </div>
      {[
        { name: "Sarah Chen", role: "Server", clockIn: "6:00 AM", hours: "9.7h", status: "active", sales: "$1,420" },
        { name: "Mike Rodriguez", role: "Server", clockIn: "6:30 AM", hours: "9.2h", status: "active", sales: "$1,185" },
        { name: "Emily Park", role: "Server", clockIn: "7:00 AM", hours: "8.7h", status: "break", sales: "$1,068" },
        { name: "James Wilson", role: "Bartender", clockIn: "10:00 AM", hours: "5.7h", status: "active", sales: "$890" },
        { name: "Aisha Patel", role: "Server", clockIn: "11:00 AM", hours: "4.7h", status: "active", sales: "$742" },
        { name: "David Kim", role: "Cashier", clockIn: "11:30 AM", hours: "4.2h", status: "break", sales: "$623" },
        { name: "Lisa Thompson", role: "Server", clockIn: "2:00 PM", hours: "1.7h", status: "active", sales: "$340" },
        { name: "Carlos Garcia", role: "Server", clockIn: "3:00 PM", hours: "0.7h", status: "active", sales: "$180" },
      ].map((s, i) => (
        <div key={i} style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 10, padding: "12px 12px", marginBottom: 5, boxShadow: C.shadow }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: s.status === "active" ? C.green : C.orange }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: C.t1 }}>{s.name}</span>
            </div>
            <span style={{ fontSize: 12, fontWeight: 600, color: C.t1 }}>{s.sales}</span>
          </div>
          <div style={{ display: "flex", gap: 5, marginLeft: 16 }}>
            <span style={{ fontSize: 9, color: C.t3, padding: "2px 6px", borderRadius: 4, background: C.hover }}>{s.role}</span>
            <span style={{ fontSize: 9, color: C.t3, padding: "2px 6px", borderRadius: 4, background: C.hover }}>In: {s.clockIn}</span>
            <span style={{ fontSize: 9, color: C.t3, padding: "2px 6px", borderRadius: 4, background: C.hover }}>{s.hours}</span>
            <span style={{ fontSize: 9, fontWeight: 600, padding: "2px 6px", borderRadius: 4, color: s.status === "active" ? C.green : C.orange, background: s.status === "active" ? C.greenSoft : C.orangeSoft }}>{s.status === "active" ? "Active" : "Break"}</span>
          </div>
        </div>
      ))}
    </div>
    <BottomNav active={0} />
  </Phone>
);

// =========== DRILL 5: VOIDS ===========
const DrillVoids = () => (
  <Phone label="DRILL: VOIDS">
    <DrillHeader title="Voids" sub="3 voids · $86 total" />
    <div style={{ flex: 1, overflowY: "auto", padding: "0 16px 16px" }}>
      <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 14, padding: 14, marginBottom: 12, boxShadow: C.shadow }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 10 }}>Void Reasons</div>
        {[{ r: "Customer changed mind", c: 1, p: 33 }, { r: "Wrong item entered", c: 1, p: 33 }, { r: "Kitchen error", c: 1, p: 33 }].map((r, i) => (
          <div key={i} style={{ marginBottom: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
              <span style={{ fontSize: 11, color: C.t2 }}>{r.r}</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: C.t1 }}>{r.c}x</span>
            </div>
            <div style={{ height: 5, background: C.hover, borderRadius: 3, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${r.p}%`, background: C.orange, borderRadius: 3, opacity: 0.6 }} />
            </div>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 8 }}>Void Log</div>
      {[
        { item: "Margherita Pizza", amount: "$15.00", time: "1:42 PM", reason: "Customer changed mind", staff: "James Wilson" },
        { item: "Caesar Salad (x2)", amount: "$22.00", time: "12:18 PM", reason: "Wrong item entered", staff: "James Wilson" },
        { item: "Fish Tacos", amount: "$14.00", time: "11:05 AM", reason: "Kitchen error", staff: "Mike Rodriguez" },
      ].map((v, i) => (
        <div key={i} style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 10, padding: "12px 12px", marginBottom: 5, boxShadow: C.shadow }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: C.t1 }}>{v.item}</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: C.orange }}>{v.amount}</span>
          </div>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.accent, marginBottom: 2 }}>{v.staff}</div>
          <div style={{ fontSize: 10, color: C.t2, marginBottom: 3 }}>{v.reason}</div>
          <span style={{ fontSize: 9, color: C.t3, padding: "2px 6px", borderRadius: 4, background: C.hover }}>{v.time}</span>
        </div>
      ))}
      <div style={{ background: C.redSoft, border: `1px solid ${C.red}33`, borderRadius: 12, padding: "12px 14px", marginTop: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
          <Icon name="warning" size={14} color={C.red} />
          <span style={{ fontSize: 11, fontWeight: 600, color: C.red }}>Attention</span>
        </div>
        <div style={{ fontSize: 11, color: C.t2, lineHeight: 1.5 }}><strong style={{ color: C.t1 }}>James Wilson</strong> has 2 of 3 voids today ($37 total). Consider reviewing with manager.</div>
      </div>
    </div>
    <BottomNav active={0} />
  </Phone>
);

// =========== DRILL 6: TIPS ===========
const DrillTips = () => (
  <Phone label="DRILL: TIPS">
    <DrillHeader title="Tips" sub="$847 total" />
    <div style={{ flex: 1, overflowY: "auto", padding: "0 16px 16px" }}>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        {[{ l: "Total Tips", v: "$847" }, { l: "Avg/Order", v: "$2.95" }, { l: "Tip Rate", v: "14.3%" }].map((k, i) => (
          <div key={i} style={{ flex: 1, background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 10, padding: "8px 6px", textAlign: "center", boxShadow: C.shadow }}>
            <div style={{ fontSize: 7, color: C.t3, textTransform: "uppercase" }}>{k.l}</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.t1, marginTop: 2 }}>{k.v}</div>
          </div>
        ))}
      </div>
      <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 14, padding: 14, marginBottom: 12, boxShadow: C.shadow }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 10 }}>Tips by Staff</div>
        {[
          { n: "Sarah Chen", tips: "$218", rate: "18.2%", val: 218 },
          { n: "Mike Rodriguez", tips: "$185", rate: "15.6%", val: 185 },
          { n: "Emily Park", tips: "$142", rate: "13.3%", val: 142 },
          { n: "James Wilson", tips: "$108", rate: "12.1%", val: 108 },
          { n: "Aisha Patel", tips: "$92", rate: "13.4%", val: 92 },
          { n: "David Kim", tips: "$58", rate: "9.3%", val: 58 },
          { n: "Lisa Thompson", tips: "$28", rate: "14.0%", val: 28 },
          { n: "Carlos Garcia", tips: "$16", rate: "12.3%", val: 16 },
        ].map((s, i) => (
          <div key={i} style={{ marginBottom: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
              <span style={{ fontSize: 11, color: C.t1, fontWeight: 500 }}>{s.n}</span>
              <div style={{ display: "flex", gap: 8 }}>
                <span style={{ fontSize: 10, color: C.t3 }}>{s.rate}</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: C.t1, width: 38, textAlign: "right" }}>{s.tips}</span>
              </div>
            </div>
            <div style={{ height: 5, background: C.hover, borderRadius: 3, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${(s.val / 218) * 100}%`, background: C.green, borderRadius: 3, opacity: 0.7 }} />
            </div>
          </div>
        ))}
      </div>
      <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 14, padding: 14, boxShadow: C.shadow }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 10 }}>Tips by Channel</div>
        {[{ c: "Dine-In", v: "$542", p: 64 }, { c: "Delivery", v: "$185", p: 22 }, { c: "Pickup", v: "$78", p: 9 }, { c: "3rd Party", v: "$42", p: 5 }].map((ch, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 0", borderBottom: i < 3 ? `1px solid ${C.divider}` : "none" }}>
            <span style={{ fontSize: 12, color: C.t1 }}>{ch.c}</span>
            <div style={{ display: "flex", gap: 10 }}>
              <span style={{ fontSize: 10, color: C.t3 }}>{ch.p}%</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: C.t1, width: 40, textAlign: "right" }}>{ch.v}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
    <BottomNav active={0} />
  </Phone>
);

// =========== SALES REPORT DATA ===========
const salesChannels = [
  {ch:"Dine-In",rev:18420,ord:412,avg:44.71,pct:38.2,d:"+5.2%",color:C.accent},
  {ch:"In-Store",rev:9680,ord:286,avg:33.85,pct:20.1,d:"+3.8%",color:"#E86B6B"},
  {ch:"Delivery",rev:7240,ord:198,avg:36.57,pct:15.0,d:"+11.4%",color:C.orange},
  {ch:"DoorDash",rev:6890,ord:184,avg:37.45,pct:14.3,d:"+8.9%",color:C.green},
  {ch:"Uber Eats",rev:6009,ord:167,avg:35.98,pct:12.4,d:"+2.1%",color:C.t3},
];
const salesStaff = [
  {n:"Maria G.",sales:8420,tips:1700,tipAvg:11.97,disc:482,refAmt:248,refCnt:15},
  {n:"David C.",sales:7180,tips:1400,tipAvg:12.73,disc:196,refAmt:230,refCnt:12},
  {n:"Sarah W.",sales:6240,tips:800,tipAvg:10.26,disc:124,refAmt:158,refCnt:8},
  {n:"James L.",sales:5680,tips:696,tipAvg:7.23,disc:368,refAmt:126,refCnt:7},
  {n:"Alex R.",sales:4840,tips:720,tipAvg:9.47,disc:312,refAmt:132,refCnt:5},
];
const salesDiscOffers = [
  {offer:"Happy Hour 20%",ord:186,disc:1284},{offer:"Employee Meal",ord:42,disc:840},
  {offer:"Loyalty $5 Off",ord:64,disc:320},{offer:"New Customer 10%",ord:28,disc:98},
];
const salesRefReasons = [
  {reason:"Wrong item",cnt:18,amt:342,staff:[{n:"Maria G.",cnt:6,amt:124},{n:"David C.",cnt:5,amt:98},{n:"Sarah W.",cnt:4,amt:72}]},
  {reason:"Changed mind",cnt:12,amt:228,staff:[{n:"Alex R.",cnt:5,amt:82},{n:"David C.",cnt:4,amt:82}]},
  {reason:"Long wait",cnt:8,amt:164,staff:[{n:"Sarah W.",cnt:4,amt:86},{n:"James L.",cnt:4,amt:78}]},
];
const salesTipChannels = [
  {ch:"Dine-In",tips:3240,pct:61,color:C.accent,staff:[{n:"Maria G.",tips:1180,avg:13.72},{n:"David C.",tips:980,avg:13.61}]},
  {ch:"Pickup",tips:860,pct:16,color:C.orange,staff:[{n:"James L.",tips:420,avg:6.56},{n:"Alex R.",tips:280,avg:6.67}]},
  {ch:"Delivery",tips:1216,pct:23,color:C.green,staff:[{n:"Maria G.",tips:520,avg:10.83},{n:"David C.",tips:420,avg:11.05}]},
];
const salesHourly = [420,890,1340,980,760,2180,3920,3640,2100,1480,1260,2840,4180,4520,3860,2640,1420,680];
const salesHourlyMax = Math.max(...salesHourly);
const salesHourLabels = ["6a","7a","8a","9a","10a","11a","12p","1p","2p","3p","4p","5p","6p","7p","8p","9p","10p","11p"];
const salesChannelList = [{name:"Dine-In",amount:"$18,420",pct:38.2,color:C.accent},{name:"In-Store",amount:"$9,680",pct:20.1,color:"#E86B6B"},{name:"Delivery",amount:"$7,240",pct:15.0,color:C.orange},{name:"DoorDash",amount:"$6,890",pct:14.3,color:C.green},{name:"Uber Eats",amount:"$6,009",pct:12.4,color:C.t3}];
const salesTopItems = [{name:"Classic Cheeseburger",qty:142,revenue:"$2,840"},{name:"Caesar Salad",qty:118,revenue:"$1,770"},{name:"Margherita Pizza",qty:96,revenue:"$1,632"},{name:"Craft Lemonade",qty:204,revenue:"$1,020"},{name:"Truffle Fries",qty:168,revenue:"$1,008"}];

// =========== REPORTS ===========
const ReportsScreen = () => {
  const [view, setView] = useState<any>(null); // null = grid, "sales" = sales report
  const [drill, setDrill] = useState<any>(null);
  const [sub, setSub] = useState<any>(null);

  const openDrill = id => { setDrill(drill === id ? null : id); setSub(null); };
  const closeDrill = () => { setDrill(null); setSub(null); };

  const categories = [
    { icon: "bar_chart",       label: "Sales",       desc: "Revenue, orders, channels & hourly trends", color: C.accent,  bg: C.accentSoft, action: "sales" },
    { icon: "restaurant_menu", label: "Product",     desc: "Item & category performance, discounts",    color: C.green,   bg: C.greenSoft },
    { icon: "receipt_long",    label: "EOD Summary", desc: "End-of-day reconciliation & funding",       color: "#7C3AED", bg: "rgba(124,58,237,0.08)" },
    { icon: "how_to_reg",      label: "Check-in",    desc: "Guest arrivals, wait times & party sizes",  color: "#0891B2", bg: "rgba(8,145,178,0.08)" },
    { icon: "event_available", label: "Reservation", desc: "Booking trends, covers & no-show rates",   color: C.orange,  bg: C.orangeSoft },
    { icon: "person",          label: "Customer",    desc: "Guest profiles, frequency & lifetime value",color: C.green,   bg: C.greenSoft },
  ];

  /* ── Mini components for drill panels ── */
  const MK = ({ label, value, color = C.t1, sub: s }: any) => (
    <div style={{ background: C.card, borderRadius: 10, padding: "10px 8px", border: `1px solid ${C.cardBorder}`, flex: 1, minWidth: 0, textAlign: "center", boxShadow: C.shadow }}>
      <div style={{ fontSize: 8, fontWeight: 500, color: C.t3, textTransform: "uppercase", letterSpacing: 0.3, marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color }}>{value}</div>
      {s && <div style={{ fontSize: 8, color: C.t3, marginTop: 2 }}>{s}</div>}
    </div>
  );

  const MC = ({ title, sub: s, children, pad = 14 }: any) => (
    <div style={{ background: C.card, borderRadius: 12, padding: pad, border: `1px solid ${C.cardBorder}`, boxShadow: C.shadow, marginBottom: 10 }}>
      {title && <div style={{ marginBottom: 10 }}><div style={{ fontSize: 13, fontWeight: 600, color: C.t1 }}>{title}</div>{s && <div style={{ fontSize: 10, color: C.t3, marginTop: 1 }}>{s}</div>}</div>}
      {children}
    </div>
  );

  const PB = ({ pct, color, label, val, max = 100 }: any) => (
    <div style={{ marginBottom: 6 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
        <span style={{ fontSize: 10, fontWeight: 500, color: C.t1 }}>{label}</span>
        <span style={{ fontSize: 10, fontWeight: 600, color: C.t1 }}>{val}</span>
      </div>
      <div style={{ height: 4, background: C.hover, borderRadius: 2 }}>
        <div style={{ height: 4, borderRadius: 2, width: `${Math.min((pct / max) * 100, 100)}%`, background: color, opacity: 0.7 }} />
      </div>
    </div>
  );

  const SI = ({ name, val, sub: s, color = C.t1, rank }: any) => (
    <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 0" }}>
      {rank !== undefined && <div style={{ width: 16, height: 16, borderRadius: 4, fontSize: 8, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", background: rank === 0 ? C.accentSoft : C.hover, color: rank === 0 ? C.accent : C.t3 }}>{rank + 1}</div>}
      <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontSize: 11, fontWeight: 600, color: C.accent, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</div></div>
      <div style={{ textAlign: "right" }}><div style={{ fontSize: 11, fontWeight: 700, color }}>{val}</div>{s && <div style={{ fontSize: 8, color: C.t3 }}>{s}</div>}</div>
    </div>
  );

  /* ── Sales drill panel renderer ── */
  const renderSalesDrill = () => {
    if (!drill) return null;

    const drillHeader = (title: any) => (
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <div onClick={closeDrill} style={{ width: 28, height: 28, borderRadius: 8, background: C.hover, border: `1px solid ${C.divider}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
          <Icon name="chevron_left" size={18} color={C.t2} />
        </div>
        <div style={{ flex: 1 }}><div style={{ fontSize: 14, fontWeight: 700, color: C.t1 }}>{title}</div></div>
      </div>
    );

    if (drill === "net_sales") return (
      <div style={{ padding: "0 16px 10px" }}>
        {drillHeader("Net Sales Drill-Down")}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginBottom: 10 }}>
          <MK label="Maghil" value="$28.7k" sub="69%" /><MK label="3rd Party" value="$12.9k" sub="31%" /><MK label="Top Seller" value="Maria G." color={C.accent} sub="$8,420" />
        </div>
        <MC title="Sales by Channel" sub="Tap to expand">
          {salesChannels.map((c, i) => (
            <div key={i} onClick={() => setSub(sub?.ch === c.ch ? null : c)} style={{ padding: "8px 0", borderBottom: i < salesChannels.length - 1 ? `1px solid ${C.divider}` : "none", background: sub?.ch === c.ch ? C.accentSoft : "transparent", borderRadius: sub?.ch === c.ch ? 6 : 0, marginLeft: sub?.ch === c.ch ? -4 : 0, paddingLeft: sub?.ch === c.ch ? 4 : 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ width: 4, height: 20, borderRadius: 2, background: c.color, opacity: 0.7 }} />
                <span style={{ fontSize: 11, fontWeight: 600, flex: 1 }}>{c.ch}</span>
                <span style={{ fontSize: 11, fontWeight: 700 }}>${c.rev.toLocaleString()}</span>
                <span style={{ fontSize: 9, color: C.t3, width: 28, textAlign: "right" }}>{c.pct}%</span>
              </div>
              {sub?.ch === c.ch && <div style={{ padding: "8px 0 0 10px", display: "flex", gap: 8 }}>
                <div style={{ fontSize: 10, color: C.t2 }}>{c.ord} orders</div><div style={{ fontSize: 10, color: C.t2 }}>AOV ${c.avg.toFixed(2)}</div><div style={{ fontSize: 10, fontWeight: 600, color: C.green }}>{c.d}</div>
              </div>}
            </div>
          ))}
        </MC>
        <MC title="Staff Sales" pad={12}>
          {salesStaff.map((s, i) => <SI key={i} rank={i} name={s.n} val={`$${s.sales.toLocaleString()}`} />)}
        </MC>
      </div>
    );

    if (drill === "discounts") return (
      <div style={{ padding: "0 16px 10px" }}>
        {drillHeader("Discount Drill-Down")}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginBottom: 10 }}>
          <MK label="Total" value="$1,482" color={C.orange} /><MK label="Disc. Orders" value="320" sub="25.7%" /><MK label="Top" value="Maria G." color={C.accent} sub="$482" />
        </div>
        <MC title="By Offer" sub="Tap for detail">
          {salesDiscOffers.map((d, i) => (
            <div key={i} onClick={() => setSub(sub?.offer === d.offer ? null : d)} style={{ padding: "8px 0", borderBottom: i < salesDiscOffers.length - 1 ? `1px solid ${C.divider}` : "none", background: sub?.offer === d.offer ? C.orangeSoft : "transparent", borderRadius: sub?.offer === d.offer ? 6 : 0 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: 11, fontWeight: 600 }}>{d.offer}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: C.orange }}>${d.disc.toLocaleString()}</span>
              </div>
              <div style={{ fontSize: 9, color: C.t3 }}>{d.ord} orders</div>
            </div>
          ))}
        </MC>
        <MC title="Staff Discounts" pad={12}>
          {[...salesStaff].sort((a, b) => b.disc - a.disc).map((s, i) => <SI key={i} rank={i} name={s.n} val={`$${s.disc}`} color={C.orange} />)}
        </MC>
      </div>
    );

    if (drill === "refunds") return (
      <div style={{ padding: "0 16px 10px" }}>
        {drillHeader("Refund Drill-Down")}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginBottom: 10 }}>
          <MK label="Total" value="$894" color={C.red} /><MK label="Count" value="47" /><MK label="Top" value="Maria G." color={C.accent} sub="$248 · 15" />
        </div>
        <MC title="By Reason" sub="Tap for staff split">
          {salesRefReasons.map((r, i) => (
            <div key={i} onClick={() => setSub(sub?.reason === r.reason ? null : r)} style={{ cursor: "pointer", marginBottom: sub?.reason === r.reason ? 8 : 0 }}>
              <PB label={r.reason} val={`$${r.amt} · ${r.cnt}`} pct={r.amt} max={342} color={C.red} />
              {sub?.reason === r.reason && <div style={{ background: C.redSoft, borderRadius: 8, padding: 8, marginTop: 4 }}>
                {r.staff.map((s, j) => <div key={j} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
                  <span style={{ fontSize: 10, fontWeight: 600, color: C.accent }}>{s.n}</span>
                  <span style={{ fontSize: 10, fontWeight: 600, color: C.red }}>${s.amt} · {s.cnt}</span>
                </div>)}
              </div>}
            </div>
          ))}
        </MC>
        <MC title="Staff Refunds" pad={12}>
          {[...salesStaff].sort((a, b) => b.refAmt - a.refAmt).map((s, i) => <SI key={i} rank={i} name={s.n} val={`$${s.refAmt}`} sub={`${s.refCnt} refunds`} color={C.red} />)}
        </MC>
      </div>
    );

    if (drill === "tips") return (
      <div style={{ padding: "0 16px 10px" }}>
        {drillHeader("Tips Drill-Down")}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginBottom: 10 }}>
          <MK label="Total" value="$5,316" color={C.green} /><MK label="Avg/Order" value="$4.26" /><MK label="Top" value="Maria G." color={C.accent} sub="$1,700" />
        </div>
        <MC title="By Channel" sub="Tap for staff">
          {salesTipChannels.map((ch, i) => (
            <div key={i} onClick={() => setSub(sub?.ch === ch.ch ? null : ch)} style={{ padding: "8px 0", borderBottom: i < salesTipChannels.length - 1 ? `1px solid ${C.divider}` : "none" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ width: 4, height: 20, borderRadius: 2, background: ch.color, opacity: 0.7 }} />
                <span style={{ fontSize: 11, fontWeight: 600, flex: 1 }}>{ch.ch} <span style={{ fontWeight: 400, color: C.t3 }}>{ch.pct}%</span></span>
                <span style={{ fontSize: 13, fontWeight: 700 }}>${ch.tips.toLocaleString()}</span>
              </div>
              {sub?.ch === ch.ch && <div style={{ padding: "6px 0 0 10px" }}>
                {ch.staff.map((s, j) => <div key={j} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0" }}>
                  <span style={{ fontSize: 10, fontWeight: 600, color: C.accent }}>{s.n}</span>
                  <span style={{ fontSize: 10, color: C.green }}>${s.tips} · Avg ${s.avg.toFixed(2)}</span>
                </div>)}
              </div>}
            </div>
          ))}
        </MC>
        <MC title="Tip Leaderboard" pad={12}>
          {[...salesStaff].sort((a, b) => b.tips - a.tips).map((s, i) => <SI key={i} rank={i} name={s.n} val={`$${s.tips.toLocaleString()}`} sub={`$${s.tipAvg} avg`} color={C.green} />)}
        </MC>
      </div>
    );

    return null;
  };

  /* ── Sales Report View ── */
  const renderSalesReport = () => (
    <>
      {/* Header */}
      <div style={{ padding: "8px 20px 6px", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div onClick={() => { setView(null); setDrill(null); setSub(null); }} style={{ width: 28, height: 28, borderRadius: 8, background: C.hover, border: `1px solid ${C.divider}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
            <Icon name="chevron_left" size={18} color={C.t2} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: C.t3 }}>Reports ›</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: C.t1 }}>Sales Report</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 8, marginLeft: 36 }}>
          <OutletDropdown />
          <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 10px", borderRadius: 8, background: C.surface, border: `1px solid ${C.divider}` }}>
            <Icon name="calendar_today" size={13} color={C.t3} />
            <span style={{ fontSize: 12, fontWeight: 600, color: C.t1 }}>Today</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 4, marginTop: 8, marginLeft: 36 }}>
          {["All", "Maghil", "3P"].map((s, i) => (
            <div key={s} style={{ padding: "3px 12px", borderRadius: 6, fontSize: 11, fontWeight: 500, background: i === 0 ? C.accentSoft : "transparent", color: i === 0 ? C.accent : C.t3 }}>{s}</div>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: "auto", paddingBottom: 8 }}>
        {drill ? renderSalesDrill() : <>
          {/* Hero */}
          <div style={{ padding: "12px 16px 0" }}>
            <div style={{ background: `linear-gradient(135deg, ${C.accentSoft}, rgba(210,58,59,0.03))`, borderRadius: 16, padding: "18px 16px", border: `1px solid rgba(210,58,59,0.15)`, marginBottom: 10, position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: -30, right: -30, width: 100, height: 100, borderRadius: "50%", background: C.accentSoft, filter: "blur(30px)" }} />
              <div style={{ fontSize: 9, fontWeight: 500, color: C.t2, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>GROSS SALES</div>
              <div style={{ fontSize: 34, fontWeight: 700, color: C.t1, letterSpacing: -1 }}>$48,239</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.green, marginTop: 4 }}>↑ +8.2% <span style={{ fontWeight: 400, color: C.t3 }}>vs prior period</span></div>
            </div>
          </div>

          {/* KPI Grid — drillable cards */}
          <div style={{ padding: "0 16px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 8 }}>
              {[
                { label: "NET SALES", value: "$41,607", delta: "+6.9%", up: true, id: "net_sales" },
                { label: "ORDERS", value: "1,247", delta: "+12.1%", up: true },
                { label: "AVG ORDER", value: "$33.36", delta: "-2.4%", up: false },
              ].map((k, i) => (
                <div key={i} onClick={k.id ? () => openDrill(k.id) : undefined} style={{ background: C.card, border: `1px solid ${k.id ? C.accent + "33" : C.cardBorder}`, borderRadius: 12, padding: "12px 10px", textAlign: "center", cursor: k.id ? "pointer" : "default", position: "relative", boxShadow: C.shadow }}>
                  {k.id && <Arrow />}
                  <div style={{ fontSize: 8, color: C.t3, textTransform: "uppercase", letterSpacing: 0.3 }}>{k.label}</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: C.t1, marginTop: 4 }}>{k.value}</div>
                  {k.delta && <div style={{ fontSize: 9, fontWeight: 600, color: k.up ? C.green : C.red, marginTop: 3 }}>{k.up ? "↑" : "↓"} {k.delta}</div>}
                </div>
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 8 }}>
              {[
                { label: "DISCOUNTS", value: "$1,482", id: "discounts" },
                { label: "REFUNDS", value: "$894", id: "refunds" },
                { label: "TIPS", value: "$5,316", id: "tips" },
              ].map((k, i) => (
                <div key={i} onClick={() => openDrill(k.id)} style={{ background: C.card, border: `1px solid ${C.accent}33`, borderRadius: 10, padding: "10px 10px", textAlign: "center", cursor: "pointer", position: "relative", boxShadow: C.shadow }}>
                  <Arrow />
                  <div style={{ fontSize: 8, color: C.t3, textTransform: "uppercase", letterSpacing: 0.3 }}>{k.label}</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: C.t1, marginTop: 3 }}>{k.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Hourly Sales */}
          <div style={{ padding: "0 16px" }}>
            <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: "12px 14px", boxShadow: C.shadow, marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: C.t1, marginBottom: 8 }}>Hourly Sales</div>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 100 }}>
                {salesHourly.map((v, i) => {
                  const h = (v / salesHourlyMax) * 80;
                  const peak = v === salesHourlyMax;
                  return (
                    <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                      <div style={{ width: "100%", maxWidth: 14, height: h, borderRadius: "3px 3px 1px 1px", background: peak ? C.accent : `rgba(210,58,59,${0.12 + (v / salesHourlyMax) * 0.4})` }} />
                      {i % 3 === 0 && <div style={{ fontSize: 7, color: C.t3 }}>{salesHourLabels[i]}</div>}
                    </div>
                  );
                })}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: 10, color: C.t3 }}>
                <span>Peak: <span style={{ color: C.accent, fontWeight: 600 }}>7 PM</span></span>
                <span>Avg: $2,292/hr</span>
              </div>
            </div>
          </div>

          {/* Channel Split */}
          <div style={{ padding: "0 16px" }}>
            <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: "12px 14px", boxShadow: C.shadow, marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: C.t1, marginBottom: 8 }}>Channel Split</div>
              {salesChannelList.map((c, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 0", borderBottom: i < salesChannelList.length - 1 ? `1px solid ${C.divider}` : "none" }}>
                  <div style={{ width: 6, height: 6, borderRadius: 2, background: c.color, opacity: 0.8 }} />
                  <span style={{ fontSize: 11, flex: 1 }}>{c.name}</span>
                  <span style={{ fontSize: 11, fontWeight: 600 }}>{c.amount}</span>
                  <span style={{ fontSize: 10, color: C.t3, width: 30, textAlign: "right" }}>{c.pct}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* Top Items */}
          <div style={{ padding: "0 16px" }}>
            <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: "12px 14px", boxShadow: C.shadow, marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: C.t1, marginBottom: 8 }}>Top Items</div>
              {salesTopItems.map((item, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: i < salesTopItems.length - 1 ? `1px solid ${C.divider}` : "none" }}>
                  <div style={{ width: 18, height: 18, borderRadius: 5, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700, background: i === 0 ? C.accentSoft : C.hover, color: i === 0 ? C.accent : C.t3 }}>{i + 1}</div>
                  <div style={{ flex: 1 }}><div style={{ fontSize: 11, fontWeight: 500 }}>{item.name}</div><div style={{ fontSize: 9, color: C.t3 }}>×{item.qty}</div></div>
                  <span style={{ fontSize: 11, fontWeight: 600 }}>{item.revenue}</span>
                </div>
              ))}
            </div>
          </div>

          {/* 3P Marketplace */}
          <div style={{ padding: "0 16px" }}>
            <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: "12px 14px", boxShadow: C.shadow, marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: C.t1, marginBottom: 8 }}>Third-Party Marketplace</div>
              {[{ name: "DoorDash", total: "$6,890", orders: 184 }, { name: "Uber Eats", total: "$6,009", orders: 156 }, { name: "Grubhub", total: "$2,840", orders: 82 }].map((mp, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: i < 2 ? `1px solid ${C.divider}` : "none" }}>
                  <span style={{ fontSize: 11, color: C.t1 }}>{mp.name}</span>
                  <div style={{ display: "flex", gap: 10 }}>
                    <span style={{ fontSize: 9, color: C.t3 }}>{mp.orders} orders</span>
                    <span style={{ fontSize: 11, fontWeight: 600 }}>{mp.total}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>}
      </div>
    </>
  );

  /* ── Main Return ── */
  return (
    <Phone label="REPORTS">
      {view === "sales" ? (
        <>
          {renderSalesReport()}
          <BottomNav active={1} />
        </>
      ) : (
        <>
          <div style={{ padding: "8px 20px 12px", flexShrink: 0 }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: C.t1 }}>Reports</div>
            <div style={{ fontSize: 11, color: C.t3, marginTop: 2 }}>Select a category to explore</div>
            <div style={{ marginTop: 10, padding: "8px 12px", borderRadius: 10, background: C.hover, border: `1px solid ${C.cardBorder}`, display: "flex", alignItems: "center", gap: 8 }}>
              <Icon name="search" size={16} color={C.t3} />
              <span style={{ fontSize: 12, color: C.t3 }}>Search reports...</span>
            </div>
          </div>
          <div style={{ flex: 1, overflowY: "auto", padding: "4px 16px 16px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {categories.map((cat, i) => {
                const isLast = i === categories.length - 1;
                const isOdd = categories.length % 2 !== 0;
                return (
                  <div key={i} onClick={cat.action ? () => setView(cat.action) : undefined} style={{ gridColumn: isLast && isOdd ? "1 / -1" : undefined, background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 14, padding: "16px 14px", cursor: "pointer", boxShadow: C.shadow, position: "relative", overflow: "hidden", display: "flex", flexDirection: "column", gap: 8 }}>
                    <div style={{ position: "absolute", top: -16, right: -16, width: 56, height: 56, borderRadius: "50%", background: cat.bg, filter: "blur(14px)", pointerEvents: "none" }} />
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: cat.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Icon name={cat.icon} size={20} color={cat.color} />
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.t1, lineHeight: 1.2 }}>{cat.label}</div>
                    <div style={{ fontSize: 10, color: C.t3, lineHeight: 1.45 }}>{cat.desc}</div>
                    <div style={{ position: "absolute", bottom: 10, right: 10, width: 18, height: 18, borderRadius: 6, background: cat.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Icon name="chevron_right" size={14} color={cat.color} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <BottomNav active={1} />
        </>
      )}
    </Phone>
  );
};

// =========== MAGHIL AI ===========
const AIScreen = () => (
  <Phone label="MAGHIL AI">
    <div style={{ padding: "8px 20px 12px", flexShrink: 0, borderBottom: `1px solid ${C.divider}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 34, height: 34, borderRadius: 10, background: `linear-gradient(135deg, ${C.accent}, ${C.purple})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 700, color: "#fff" }}>M</div>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.t1 }}>Brisque AI</div>
          <div style={{ fontSize: 10, color: C.green, display: "flex", alignItems: "center", gap: 4 }}>
            <div style={{ width: 5, height: 5, borderRadius: "50%", background: C.green }} />Online
          </div>
        </div>
      </div>
    </div>
    <div style={{ flex: 1, overflowY: "auto", padding: "16px 16px" }}>
      <div style={{ background: `linear-gradient(135deg, ${C.accent}08, ${C.purple}06)`, border: `1px solid ${C.accent}18`, borderRadius: 16, padding: 18, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.t1, marginBottom: 6 }}>Hey James!</div>
        <div style={{ fontSize: 12, color: C.t2, lineHeight: 1.6 }}>I can answer questions about your restaurant data. Try asking me about sales trends, menu performance, staff metrics, or anything from your reports.</div>
      </div>
      <div style={{ fontSize: 10, color: C.t3, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Suggested Questions</div>
      {["What were my top 5 selling items last week?", "Which staff member had the most voids today?", "How does DoorDash revenue compare to Uber Eats?", "What's my busiest hour on Saturdays?", "Show me this month's refund rate trend"].map((q, i) => (
        <div key={i} style={{ padding: "10px 14px", borderRadius: 10, background: C.card, border: `1px solid ${C.cardBorder}`, marginBottom: 6, display: "flex", alignItems: "center", gap: 8, boxShadow: C.shadow }}>
          <Icon name="arrow_forward" size={13} color={C.accent} />
          <span style={{ fontSize: 12, color: C.t2 }}>{q}</span>
        </div>
      ))}
      <div style={{ marginTop: 16 }}>
        <div style={{ fontSize: 10, color: C.t3, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Recent</div>
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
          <div style={{ maxWidth: "80%", padding: "10px 14px", borderRadius: "14px 14px 4px 14px", background: C.accent, fontSize: 12, color: "#fff", lineHeight: 1.5 }}>What was yesterday's top revenue channel?</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ width: 24, height: 24, borderRadius: 6, background: `linear-gradient(135deg, ${C.accent}, ${C.purple})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: "#fff", flexShrink: 0, marginTop: 2 }}>M</div>
          <div style={{ maxWidth: "85%", padding: "12px 14px", borderRadius: "14px 14px 14px 4px", background: C.card, border: `1px solid ${C.cardBorder}`, fontSize: 12, color: C.t2, lineHeight: 1.6, boxShadow: C.shadow }}>
            <span style={{ color: C.t1, fontWeight: 500 }}>Dine-In</span> was your top channel yesterday with <span style={{ color: C.accent, fontWeight: 600 }}>$3,842</span> in revenue (44% of total). Delivery came in second at <span style={{ color: C.green, fontWeight: 600 }}>$2,180</span>.
            <div style={{ marginTop: 8, padding: "8px 10px", borderRadius: 8, background: C.hover, fontSize: 10, color: C.t3, display: "flex", alignItems: "center", gap: 5 }}>
              <Icon name="database" size={11} color={C.t3} />
              Source: mh_summary_daily_sales · Yesterday
            </div>
          </div>
        </div>
      </div>
    </div>
    <div style={{ padding: "8px 16px 20px", flexShrink: 0, borderTop: `1px solid ${C.divider}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderRadius: 12, background: C.hover, border: `1px solid ${C.cardBorder}` }}>
        <span style={{ flex: 1, fontSize: 13, color: C.t3 }}>Ask about your data...</span>
        <div style={{ width: 32, height: 32, borderRadius: 8, background: C.accent, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon name="arrow_upward" size={16} color="#fff" />
        </div>
      </div>
    </div>
    <BottomNav active={2} />
  </Phone>
);

// =========== SUPPORT ===========
const SUPPORT_ITEMS = [
  { icon: "bar_chart",       label: "Reports not loading"   },
  { icon: "credit_card",     label: "Payment discrepancy"   },
  { icon: "manage_accounts", label: "Account & permissions" },
  { icon: "build",           label: "POS integration issue" },
];

const SupportScreen = () => (
  <Phone label="SUPPORT">
    <div style={{ padding: "8px 20px 12px", flexShrink: 0, borderBottom: `1px solid ${C.divider}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 34, height: 34, borderRadius: 10, background: C.green, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon name="support_agent" size={20} color="#fff" />
        </div>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.t1 }}>Support</div>
          <div style={{ fontSize: 10, color: C.green, display: "flex", alignItems: "center", gap: 4 }}>
            <div style={{ width: 5, height: 5, borderRadius: "50%", background: C.green }} />Typically replies in 2 min
          </div>
        </div>
      </div>
    </div>
    <div style={{ flex: 1, overflowY: "auto", padding: "16px 16px" }}>
      <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 14, padding: 16, marginBottom: 16, boxShadow: C.shadow }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.t1, marginBottom: 10 }}>Quick Help</div>
        {SUPPORT_ITEMS.map((item, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: i < 3 ? `1px solid ${C.divider}` : "none" }}>
            <Icon name={item.icon} size={18} color={C.t3} />
            <span style={{ fontSize: 12, color: C.t2, flex: 1 }}>{item.label}</span>
            <Icon name="chevron_right" size={16} color={C.t3} />
          </div>
        ))}
      </div>
      <div style={{ fontSize: 10, color: C.t3, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Live Chat</div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <div style={{ width: 28, height: 28, borderRadius: "50%", background: C.green, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0 }}>R</div>
        <div>
          <div style={{ fontSize: 10, color: C.t3, marginBottom: 3 }}>Rachel · Maghil Support</div>
          <div style={{ padding: "10px 14px", borderRadius: "14px 14px 14px 4px", background: C.card, border: `1px solid ${C.cardBorder}`, fontSize: 12, color: C.t2, lineHeight: 1.5, boxShadow: C.shadow }}>Hi James! Welcome to Maghil Support. How can I help you today?</div>
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
        <div style={{ maxWidth: "80%", padding: "10px 14px", borderRadius: "14px 14px 4px 14px", background: C.accent, fontSize: 12, color: "#fff", lineHeight: 1.5 }}>Hi! My EOD report is showing a variance of $45. Can you help me figure out where the mismatch is?</div>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <div style={{ width: 28, height: 28, borderRadius: "50%", background: C.green, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0 }}>R</div>
        <div>
          <div style={{ fontSize: 10, color: C.t3, marginBottom: 3 }}>Rachel · Maghil Support</div>
          <div style={{ padding: "10px 14px", borderRadius: "14px 14px 14px 4px", background: C.card, border: `1px solid ${C.cardBorder}`, fontSize: 12, color: C.t2, lineHeight: 1.5, boxShadow: C.shadow }}>Of course! Let me pull up your EOD for today. A $45 variance usually comes from one of three things: pending card settlements, tip adjustments post-close, or a refund processed after the snapshot. Give me one moment to check...</div>
        </div>
      </div>
    </div>
    <div style={{ padding: "8px 16px 20px", flexShrink: 0, borderTop: `1px solid ${C.divider}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderRadius: 12, background: C.hover, border: `1px solid ${C.cardBorder}` }}>
        <Icon name="attach_file" size={18} color={C.t3} />
        <span style={{ flex: 1, fontSize: 13, color: C.t3 }}>Type a message...</span>
        <div style={{ width: 32, height: 32, borderRadius: 8, background: C.green, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon name="arrow_upward" size={16} color="#fff" />
        </div>
      </div>
    </div>
    <BottomNav active={3} />
  </Phone>
);

// =========== PRODUCT & MENU PERFORMANCE DATA ===========
const menuTopItems = [
  { name: "Classic Cheeseburger", revenue: 18420, qty: 1842 },
  { name: "Margherita Pizza", revenue: 15890, qty: 1324 },
  { name: "Grilled Chicken Bowl", revenue: 13250, qty: 1105 },
  { name: "Fish & Chips", revenue: 11780, qty: 982 },
  { name: "Caesar Salad", revenue: 9340, qty: 1168 },
  { name: "BBQ Ribs Platter", revenue: 8920, qty: 594 },
  { name: "Shrimp Tacos", revenue: 8150, qty: 815 },
  { name: "Mushroom Risotto", revenue: 7630, qty: 636 },
  { name: "Buffalo Wings", revenue: 7210, qty: 1202 },
  { name: "Truffle Fries", revenue: 6890, qty: 1378 },
];
const menuBottomItems = [
  { name: "House Salad", revenue: 890, qty: 178 },
  { name: "Breadstick Basket", revenue: 1120, qty: 448 },
  { name: "Soup of the Day", revenue: 1340, qty: 268 },
  { name: "Kids Mac & Cheese", revenue: 1580, qty: 395 },
  { name: "Seasonal Sorbet", revenue: 1690, qty: 338 },
];
const menuCategoryData = [
  { name: "Entrées", value: 48200, pct: 38.2 },
  { name: "Appetizers", value: 22100, pct: 17.5 },
  { name: "Sides", value: 18900, pct: 15.0 },
  { name: "Beverages", value: 16300, pct: 12.9 },
  { name: "Desserts", value: 11800, pct: 9.4 },
  { name: "Kids Menu", value: 8900, pct: 7.0 },
];
const menuCatColors = ["#D23A3B", "#E86B6B", "#F09494", "#D97706", "#16A34A", "#9CA3B8"];
const menuPmixData = [
  { name: "Classic Cheeseburger", pct: 14.2 }, { name: "Truffle Fries", pct: 10.6 },
  { name: "Margherita Pizza", pct: 10.2 }, { name: "Buffalo Wings", pct: 9.3 },
  { name: "Caesar Salad", pct: 9.0 }, { name: "Grilled Chicken Bowl", pct: 8.5 },
  { name: "Fish & Chips", pct: 7.6 }, { name: "Shrimp Tacos", pct: 6.3 },
  { name: "Mushroom Risotto", pct: 4.9 }, { name: "BBQ Ribs Platter", pct: 4.6 },
].sort((a, b) => b.pct - a.pct);
const menuChannelItems = [
  { name: "Classic Cheeseburger", dinein: 7800, delivery: 4200, doordash: 3800, ubereats: 2620 },
  { name: "Margherita Pizza", dinein: 5200, delivery: 3900, doordash: 4100, ubereats: 2690 },
  { name: "Grilled Chicken Bowl", dinein: 3100, delivery: 5400, doordash: 2850, ubereats: 1900 },
  { name: "Fish & Chips", dinein: 6200, delivery: 2100, doordash: 2080, ubereats: 1400 },
  { name: "Caesar Salad", dinein: 4800, delivery: 1900, doordash: 1540, ubereats: 1100 },
];
const menuChColors = { dinein: "#D23A3B", delivery: "#E86B6B", doordash: "#D97706", ubereats: "#16A34A" };
const menuAddonData = [
  { name: "Classic Cheeseburger", addon: 3240, rate: 68 },
  { name: "Buffalo Wings", addon: 2180, rate: 72 },
  { name: "Grilled Chicken Bowl", addon: 1960, rate: 55 },
  { name: "Margherita Pizza", addon: 1740, rate: 48 },
  { name: "Fish & Chips", addon: 1520, rate: 42 },
];
const menuRefundData = [
  { name: "Caesar Salad", rate: 8.2, qty: 96, reason: "Wrong order" },
  { name: "Fish & Chips", rate: 6.1, qty: 60, reason: "Quality complaint" },
  { name: "Soup of the Day", rate: 5.8, qty: 16, reason: "Cold food" },
  { name: "Mushroom Risotto", rate: 4.3, qty: 27, reason: "Long wait" },
  { name: "Shrimp Tacos", rate: 3.9, qty: 32, reason: "Missing item" },
];
const menuAllItems = [...menuTopItems, ...menuBottomItems].map(i => i.name);
const menuAllCategories = ["Entrées", "Appetizers", "Sides", "Beverages", "Desserts", "Kids Menu"];
const menuTrendColors = [C.accent, C.orange, C.green, "#7C5CFC", "#0EA5E9"];
const menuTrendData = (() => {
  const b = {}; menuAllItems.forEach(n => { b[n] = 100 + Math.floor(Math.random() * 500); });
  const d = [];
  for (let i = 29; i >= 0; i--) {
    const dt = new Date(2026, 2, 6); dt.setDate(dt.getDate() - i);
    const r = { date: `${dt.getMonth() + 1}/${dt.getDate()}` };
    menuAllItems.forEach(n => { r[n] = b[n] + Math.floor(Math.random() * 150); }); d.push(r);
  }
  return d;
})();
const menuTableData = menuTopItems.map((item, i) => ({
  ...item,
  category: ["Entrées","Entrées","Entrées","Entrées","Appetizers","Entrées","Entrées","Entrées","Appetizers","Sides"][i],
  pmix: menuPmixData.find(p => p.name === item.name)?.pct || 0,
  addonRev: menuAddonData.find(a => a.name === item.name)?.addon || 0,
  refundQty: menuRefundData.find(r => r.name === item.name)?.qty || 0,
}));
const menuFmtFull = (n: any) => `$${n.toLocaleString()}`;
const menuFmtNum = (n: any) => n.toLocaleString();

// ─── Mobile Search Select (Bottom Sheet) ─────────────────────────────────────
const MMenuSearchSelect = ({ label, options, selected, onChange, placeholder = "Search..." }: any) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const filtered = options.filter(o => o.toLowerCase().includes(search.toLowerCase()));
  const toggle = (val: any) => onChange(selected.includes(val) ? selected.filter(s => s !== val) : [...selected, val]);
  return (
    <div>
      <button onClick={(e) => { e.stopPropagation(); setOpen(true); }} style={{
        display: "flex", alignItems: "center", gap: 4, padding: "5px 10px", borderRadius: 7,
        border: `1px solid ${selected.length > 0 ? C.accent : C.cardBorder}`,
        background: selected.length > 0 ? C.accentSoft : C.surface,
        fontSize: 10, fontWeight: 500, color: selected.length > 0 ? C.accent : C.t2,
        fontFamily: "'DM Sans', sans-serif", cursor: "pointer", whiteSpace: "nowrap",
      }}>
        {selected.length === 0 ? label : selected.length === 1 ? (selected[0].length > 14 ? selected[0].slice(0,14)+"…" : selected[0]) : `${selected.length} sel.`}
        <span style={{ fontSize: 7, color: C.t3 }}>▼</span>
      </button>
      {open && (
        <div style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", flexDirection: "column" }}>
          <div style={{ flex: 1, background: "rgba(0,0,0,0.3)" }} onClick={() => { setOpen(false); setSearch(""); }} />
          <div style={{ background: C.surface, borderRadius: "16px 16px 0 0", maxHeight: "60vh", display: "flex", flexDirection: "column", boxShadow: "0 -4px 20px rgba(0,0,0,0.10)" }}>
            <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 6px" }}>
              <div style={{ width: 36, height: 4, borderRadius: 2, background: C.divider }} />
            </div>
            <div style={{ padding: "4px 16px 10px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: C.t1 }}>{label}</span>
              <button onClick={() => { setOpen(false); setSearch(""); }} style={{ border: "none", background: "none", fontSize: 13, color: C.accent, fontWeight: 600, fontFamily: "'DM Sans',sans-serif", cursor: "pointer" }}>Done</button>
            </div>
            <div style={{ padding: "0 16px 10px" }}>
              <input autoFocus value={search} onChange={e => setSearch(e.target.value)} placeholder={placeholder}
                style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px", borderRadius: 10, border: `1px solid ${C.cardBorder}`, fontSize: 13, fontFamily: "'DM Sans',sans-serif", color: C.t1, background: C.bg, outline: "none" }} />
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "0 8px 16px" }}>
              {filtered.length === 0 && <div style={{ padding: "12px 16px", fontSize: 12, color: C.t3 }}>No results</div>}
              {filtered.map(opt => {
                const isSel = selected.includes(opt);
                return (
                  <div key={opt} onClick={() => toggle(opt)} style={{
                    padding: "10px 12px", fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 10, borderRadius: 8,
                    background: isSel ? C.accentSoft : "transparent", color: isSel ? C.accent : C.t1, fontWeight: isSel ? 600 : 400, marginBottom: 2,
                  }}>
                    <span style={{ width: 18, height: 18, borderRadius: 4, border: `1.5px solid ${isSel ? C.accent : C.cardBorder}`, background: isSel ? C.accent : "transparent", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#fff", flexShrink: 0 }}>{isSel ? "✓" : ""}</span>
                    <span>{opt}</span>
                  </div>
                );
              })}
            </div>
            {selected.length > 0 && (
              <div style={{ padding: "8px 16px 16px", borderTop: `1px solid ${C.divider}` }}>
                <button onClick={() => { onChange([]); setSearch(""); }} style={{ width: "100%", padding: "10px", borderRadius: 8, border: "none", background: C.bg, fontSize: 12, fontWeight: 600, color: C.accent, fontFamily: "'DM Sans',sans-serif", cursor: "pointer" }}>Clear All</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Mobile Export Action Sheet ──────────────────────────────────────────────
const menuExportCSV = (filename: any, headers: any, rows: any) => {
  const esc = (v: any) => { const s = String(v ?? ""); return s.includes(",") || s.includes('"') ? `"${s.replace(/"/g,'""')}"` : s; };
  const csv = [headers.map(esc).join(","), ...rows.map(r => r.map(esc).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" }); const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = `${filename}.csv`; a.click(); URL.revokeObjectURL(url);
};
const menuExportPDF = (title: any, headers: any, rows: any) => {
  const thS = `style="border:1px solid #E2E5EC;padding:6px 8px;text-align:left;font-size:10px;font-weight:600;color:#5A6178;background:#F5F6FA"`;
  const tdS = `style="border:1px solid #E2E5EC;padding:6px 8px;font-size:11px;color:#1A1D2E"`;
  const html = `<!DOCTYPE html><html><head><title>${title}</title><link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet"><style>body{font-family:'DM Sans',sans-serif;padding:20px;color:#1A1D2E}h1{font-size:16px;font-weight:700;margin-bottom:4px}.sub{font-size:11px;color:#5A6178;margin-bottom:16px}table{width:100%;border-collapse:collapse}@media print{button{display:none!important}}</style></head><body><div style="display:flex;align-items:center;gap:8px;margin-bottom:16px"><div style="width:24px;height:24px;border-radius:6px;background:linear-gradient(135deg,#D23A3B,#E86B6B);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:12px">M</div><div><span style="font-size:13px;font-weight:700">Maghil</span> <span style="font-size:8px;color:#9CA3B8;letter-spacing:0.5px">ANALYTICS</span></div></div><h1>${title}</h1><div class="sub">Exported ${new Date().toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}</div><table><thead><tr>${headers.map((h: any)=>`<th ${thS}>${h}</th>`).join("")}</tr></thead><tbody>${rows.map((r: any)=>`<tr>${r.map((c: any)=>`<td ${tdS}>${c}</td>`).join("")}</tr>`).join("")}</tbody></table><br/><button onclick="window.print()" style="padding:8px 16px;background:#D23A3B;color:#fff;border:none;border-radius:6px;font-family:'DM Sans';font-size:11px;font-weight:600;cursor:pointer">Print / Save PDF</button></body></html>`;
  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, "_blank");
  if (w) {
    w.onload = () => URL.revokeObjectURL(url);
  } else {
    const a = document.createElement("a");
    a.href = url;
    a.download = `${title.replace(/\s+/g, "_")}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
};

const MMenuExportBtn = ({ onCSV, onPDF }: any) => {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button onClick={(e) => { e.stopPropagation(); setOpen(true); }} style={{ width: 26, height: 26, borderRadius: 7, border: `1px solid ${C.cardBorder}`, background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
        <Icon name="download" size={13} color={C.t3} />
      </button>
      {open && (
        <div style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", flexDirection: "column" }} onClick={() => setOpen(false)}>
          <div style={{ flex: 1, background: "rgba(0,0,0,0.3)" }} />
          <div style={{ background: C.surface, borderRadius: "16px 16px 0 0", padding: "0 0 24px", boxShadow: "0 -4px 20px rgba(0,0,0,0.10)" }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 6px" }}>
              <div style={{ width: 36, height: 4, borderRadius: 2, background: C.divider }} />
            </div>
            <div style={{ padding: "4px 20px 14px", fontSize: 14, fontWeight: 700, color: C.t1 }}>Export</div>
            <div onClick={() => { onCSV(); setOpen(false); }} style={{ padding: "14px 20px", display: "flex", alignItems: "center", gap: 12, cursor: "pointer", fontSize: 14, color: C.t1 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: C.greenSoft, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Icon name="table_chart" size={18} color={C.green} />
              </div>
              <div><div style={{ fontWeight: 600 }}>Export as CSV</div><div style={{ fontSize: 11, color: C.t3, marginTop: 2 }}>Spreadsheet-ready format</div></div>
            </div>
            <div style={{ height: 1, background: C.divider, margin: "0 20px" }} />
            <div onClick={() => { onPDF(); setOpen(false); }} style={{ padding: "14px 20px", display: "flex", alignItems: "center", gap: 12, cursor: "pointer", fontSize: 14, color: C.t1 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: C.redSoft, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Icon name="picture_as_pdf" size={18} color={C.accent} />
              </div>
              <div><div style={{ fontWeight: 600 }}>Export as PDF</div><div style={{ fontSize: 11, color: C.t3, marginTop: 2 }}>Print-ready document</div></div>
            </div>
            <div style={{ padding: "12px 20px 0" }}>
              <button onClick={() => setOpen(false)} style={{ width: "100%", padding: 12, borderRadius: 10, border: `1px solid ${C.cardBorder}`, background: C.surface, fontSize: 14, fontWeight: 600, color: C.t2, fontFamily: "'DM Sans',sans-serif", cursor: "pointer" }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Mobile Tooltip ──────────────────────────────────────────────────────────
const MMenuTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.cardBorder}`, borderRadius: 8, padding: "6px 10px", boxShadow: C.shadow, fontFamily: "'DM Sans',sans-serif" }}>
      {label && <div style={{ fontSize: 10, fontWeight: 600, color: C.t1, marginBottom: 2 }}>{label}</div>}
      {payload.map((p, i) => (
        <div key={i} style={{ fontSize: 10, color: C.t2, display: "flex", alignItems: "center", gap: 4, marginTop: 1 }}>
          <span style={{ width: 6, height: 6, borderRadius: 1.5, background: p.color || C.accent, display: "inline-block" }} />
          {p.name}: {menuFmtFull(p.value)}
        </div>
      ))}
    </div>
  );
};

// =========== PRODUCT & MENU PERFORMANCE SCREEN ===========
const ProductMenuScreen = () => {
  const [sellerView, setSellerView] = useState("top");
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [trendItems, setTrendItems] = useState(["Classic Cheeseburger", "Margherita Pizza"]);
  const [tableCatFilter, setTableCatFilter] = useState([]);
  const [tableItemFilter, setTableItemFilter] = useState([]);

  const filteredTable = useMemo(() => {
    let d = menuTableData;
    if (tableCatFilter.length > 0) d = d.filter(r => tableCatFilter.includes(r.category));
    if (tableItemFilter.length > 0) d = d.filter(r => tableItemFilter.includes(r.name));
    return d;
  }, [tableCatFilter, tableItemFilter]);

  const sellerData = sellerView === "top" ? menuTopItems : menuBottomItems;
  const maxRev = Math.max(...sellerData.map(d => d.revenue));

  return (
    <Phone label="PRODUCT & MENU PERFORMANCE">
      {/* Drill Header */}
      <div style={{ padding: "8px 20px 10px", flexShrink: 0, borderBottom: `1px solid ${C.divider}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <div style={{ width: 28, height: 28, borderRadius: 8, background: C.hover, border: `1px solid ${C.divider}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon name="chevron_left" size={18} color={C.t2} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: C.t3 }}>Reports › Menu</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: C.t1 }}>Product & Menu</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 6 }}>
          {[["1d","Today"],["7d","7D"],["30d","30D"],["mtd","MTD"]].map(([k,l]) => (
            <div key={k} style={{ padding: "4px 10px", borderRadius: 7, fontSize: 10, fontWeight: 500, background: k === "7d" ? C.accentSoft : "transparent", color: k === "7d" ? C.accent : C.t3 }}>{l}</div>
          ))}
          <div style={{ marginLeft: "auto" }}><OutletDropdown /></div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div style={{ flex: 1, overflowY: "auto", padding: "8px 16px 16px", display: "flex", flexDirection: "column", gap: 8 }}>

        {/* KPI Strip */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
          <KPI label="Item Revenue" value="$126.2K" delta="8.3%" up={true} />
          <KPI label="Items Sold" value="12,963" delta="5.1%" up={true} />
          <KPI label="Avg / Item" value="$9.74" delta="3.0%" up={true} />
        </div>

        {/* Top / Bottom Items */}
        <div style={{ background: C.card, border: `1px solid ${C.accent}33`, borderRadius: 14, padding: 14, position: "relative", boxShadow: C.shadow }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: C.t1 }}>{sellerView === "top" ? "Top 10 by Revenue" : "Bottom 5 by Revenue"}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              {["top","bottom"].map(v => (
                <button key={v} onClick={() => setSellerView(v)} style={{ padding: "3px 8px", borderRadius: 5, border: "none", cursor: "pointer", fontSize: 9, fontWeight: 500, fontFamily: "'DM Sans',sans-serif", background: sellerView === v ? C.accentSoft : "transparent", color: sellerView === v ? C.accent : C.t3 }}>{v === "top" ? "Top 10" : "Bottom 5"}</button>
              ))}
              <MMenuExportBtn
                onCSV={() => menuExportCSV(sellerView === "top" ? "Top_10_Revenue" : "Bottom_5_Revenue", ["Item","Revenue","Qty"], sellerData.map(r => [r.name, r.revenue, r.qty]))}
                onPDF={() => menuExportPDF(sellerView === "top" ? "Top 10 Items by Revenue" : "Bottom 5 Items by Revenue", ["Item","Revenue","Qty"], sellerData.map(r => [r.name, menuFmtFull(r.revenue), menuFmtNum(r.qty)]))}
              />
            </div>
          </div>
          {sellerData.map((item, i) => (
            <div key={i} onClick={() => setSelectedItem(item)} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7, cursor: "pointer" }}>
              <div style={{ width: 18, height: 18, borderRadius: 4, background: i < 3 ? C.accentSoft : C.hover, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, fontWeight: 600, color: i < 3 ? C.accent : C.t3, flexShrink: 0 }}>{i + 1}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, fontWeight: 500, color: C.t1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: 3 }}>{item.name}</div>
                <div style={{ background: C.hover, borderRadius: 2.5, height: 5, overflow: "hidden" }}>
                  <div style={{ width: `${(item.revenue / maxRev) * 100}%`, height: "100%", background: sellerView === "top" ? C.accent : C.red, borderRadius: 2.5, opacity: 0.7 }} />
                </div>
              </div>
              <div style={{ fontSize: 11, fontWeight: 600, color: C.t1, flexShrink: 0 }}>{menuFmtFull(item.revenue)}</div>
            </div>
          ))}
        </div>

        {/* Category Breakdown */}
        <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 14, padding: 14, boxShadow: C.shadow }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 8 }}>Category Breakdown</div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie data={menuCategoryData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={65} paddingAngle={2} strokeWidth={0}>
                  {menuCategoryData.map((_, i) => <Cell key={i} fill={menuCatColors[i]} />)}
                </Pie>
                <Tooltip content={<MMenuTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px" }}>
            {menuCategoryData.map((cat, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10 }}>
                <span style={{ width: 7, height: 7, borderRadius: 2, background: menuCatColors[i], display: "inline-block" }} />
                <span style={{ color: C.t2 }}>{cat.name}</span>
                <span style={{ fontWeight: 600, color: C.t1 }}>{cat.pct}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* PMIX */}
        <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 14, padding: 14, boxShadow: C.shadow }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: C.t1 }}>Product Mix (PMIX)</div>
            <MMenuExportBtn
              onCSV={() => menuExportCSV("Product_Mix_PMIX", ["Item","PMIX %"], menuPmixData.map(r => [r.name, `${r.pct.toFixed(1)}%`]))}
              onPDF={() => menuExportPDF("Product Mix (PMIX)", ["Item","PMIX %"], menuPmixData.map(r => [r.name, `${r.pct.toFixed(1)}%`]))}
            />
          </div>
          {menuPmixData.slice(0, 8).map((item, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <div style={{ width: 90, fontSize: 10, fontWeight: 500, color: C.t1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flexShrink: 0 }}>{item.name}</div>
              <div style={{ flex: 1, background: C.hover, borderRadius: 2.5, height: 5, overflow: "hidden" }}>
                <div style={{ width: `${(item.pct / 16) * 100}%`, height: "100%", background: C.accent, borderRadius: 2.5 }} />
              </div>
              <div style={{ width: 34, fontSize: 10, fontWeight: 600, color: C.t1, textAlign: "right" }}>{item.pct}%</div>
            </div>
          ))}
        </div>

        {/* Channel Split */}
        <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 14, padding: 14, boxShadow: C.shadow }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 8 }}>Revenue by Channel</div>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={menuChannelItems.slice(0, 4)} layout="vertical" margin={{ left: 75, right: 5, top: 0, bottom: 0 }}>
              <XAxis type="number" tick={{ fontSize: 8, fill: C.t3 }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v/1000).toFixed(0)}K`} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 9, fill: C.t1 }} axisLine={false} tickLine={false} width={75} />
              <Tooltip content={<MMenuTooltip />} />
              <Bar dataKey="dinein" name="Dine-In" stackId="a" fill={menuChColors.dinein} />
              <Bar dataKey="delivery" name="Delivery" stackId="a" fill={menuChColors.delivery} />
              <Bar dataKey="doordash" name="DoorDash" stackId="a" fill={menuChColors.doordash} />
              <Bar dataKey="ubereats" name="UberEats" stackId="a" fill={menuChColors.ubereats} radius={[0,3,3,0]} />
            </BarChart>
          </ResponsiveContainer>
          <div style={{ display: "flex", justifyContent: "center", gap: 10, marginTop: 4 }}>
            {[{l:"Dine-In",c:menuChColors.dinein},{l:"Delivery",c:menuChColors.delivery},{l:"DoorDash",c:menuChColors.doordash},{l:"UberEats",c:menuChColors.ubereats}].map((ch,i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 8, color: C.t3 }}><span style={{ width: 6, height: 6, borderRadius: 1.5, background: ch.c, display: "inline-block" }} />{ch.l}</div>
            ))}
          </div>
        </div>

        {/* Add-On Revenue */}
        <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 14, padding: 14, boxShadow: C.shadow }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 10 }}>Add-On Revenue</div>
          {menuAddonData.map((item, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7 }}>
              <div style={{ flex: 1, fontSize: 10, fontWeight: 500, color: C.t1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</div>
              <div style={{ fontSize: 10, fontWeight: 600, color: C.t1 }}>{menuFmtFull(item.addon)}</div>
              <div style={{ fontSize: 9, fontWeight: 500, color: C.accent, background: C.accentSoft, borderRadius: 4, padding: "2px 5px" }}>{item.rate}%</div>
            </div>
          ))}
        </div>

        {/* Refund & Void */}
        <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 14, padding: 14, boxShadow: C.shadow }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 10 }}>Refund & Void Highlights</div>
          {menuRefundData.map((item, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 10, fontWeight: 500, color: C.t1, marginBottom: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</div>
                <div style={{ background: C.hover, borderRadius: 2.5, height: 5, overflow: "hidden" }}>
                  <div style={{ width: `${(item.rate / 10) * 100}%`, height: "100%", background: C.red, borderRadius: 2.5, opacity: 0.6 }} />
                </div>
              </div>
              <div style={{ fontSize: 10, fontWeight: 600, color: C.red, flexShrink: 0 }}>{item.rate}%</div>
              <div style={{ fontSize: 8, fontWeight: 500, color: C.orange, background: C.orangeSoft, borderRadius: 4, padding: "2px 5px", whiteSpace: "nowrap", flexShrink: 0 }}>{item.reason}</div>
            </div>
          ))}
        </div>

        {/* Revenue Trend */}
        <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 14, padding: 14, boxShadow: C.shadow }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: C.t1 }}>Revenue Trend — 30D</div>
            <MMenuSearchSelect label="Items" options={menuAllItems} selected={trendItems} onChange={(v) => setTrendItems(v.slice(0,5))} placeholder="Search items..." />
          </div>
          {trendItems.length === 0 ? (
            <div style={{ height: 120, display: "flex", alignItems: "center", justifyContent: "center", color: C.t3, fontSize: 11 }}>Select items to view</div>
          ) : (
            <ResponsiveContainer width="100%" height={140}>
              <LineChart data={menuTrendData} margin={{ left: 0, right: 5, top: 5, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.divider} />
                <XAxis dataKey="date" tick={{ fontSize: 7, fill: C.t3 }} axisLine={false} tickLine={false} interval={6} />
                <YAxis tick={{ fontSize: 7, fill: C.t3 }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}`} width={32} />
                <Tooltip content={<MMenuTooltip />} />
                {trendItems.map((name, i) => (
                  <Line key={name} type="monotone" dataKey={name} name={name} stroke={menuTrendColors[i % menuTrendColors.length]} strokeWidth={1.5} dot={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
          {trendItems.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "3px 10px", marginTop: 4 }}>
              {trendItems.map((name, i) => (
                <div key={name} style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 8, color: C.t2 }}>
                  <span style={{ width: 8, height: 2.5, borderRadius: 1, background: menuTrendColors[i % menuTrendColors.length], display: "inline-block" }} />
                  {name.length > 16 ? name.slice(0, 16) + "…" : name}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Full Item Performance — Card List */}
        <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 14, padding: 14, boxShadow: C.shadow }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: C.t1 }}>Item Performance</div>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ fontSize: 9, color: C.t3 }}>{filteredTable.length}/{menuTableData.length}</span>
              <MMenuExportBtn
                onCSV={() => menuExportCSV("Item_Performance", ["Item","Category","Qty","Revenue","PMIX%","AddOn","Refunds"], filteredTable.map(r => [r.name, r.category, r.qty, r.revenue, `${r.pmix.toFixed(1)}%`, r.addonRev||"—", r.refundQty]))}
                onPDF={() => menuExportPDF("Item Performance", ["Item","Category","Qty","Revenue","PMIX%","AddOn","Refunds"], filteredTable.map(r => [r.name, r.category, menuFmtNum(r.qty), menuFmtFull(r.revenue), `${r.pmix.toFixed(1)}%`, r.addonRev ? menuFmtFull(r.addonRev) : "—", r.refundQty||"0"]))}
              />
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
            <MMenuSearchSelect label="Category" options={menuAllCategories} selected={tableCatFilter} onChange={setTableCatFilter} placeholder="Search categories..." />
            <MMenuSearchSelect label="Item" options={menuAllItems} selected={tableItemFilter} onChange={setTableItemFilter} placeholder="Type to search items..." />
          </div>
          {filteredTable.map((item, i) => (
            <div key={i} onClick={() => setSelectedItem(item)} style={{
              padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.accent}25`,
              background: C.card, cursor: "pointer", position: "relative", marginBottom: 6,
            }}>
              <svg width="8" height="8" viewBox="0 0 10 10" style={{ position: "absolute", top: 8, right: 8, opacity: 0.35 }}>
                <path d="M3.5 2L7 5L3.5 8" fill="none" stroke={C.accent} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: C.t1 }}>{item.name}</div>
                  <div style={{ fontSize: 9, color: C.t3, marginTop: 1 }}>{item.category}</div>
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.t1 }}>{menuFmtFull(item.revenue)}</div>
              </div>
              <div style={{ display: "flex", gap: 12 }}>
                <span style={{ fontSize: 9, color: C.t3 }}>Qty <span style={{ fontWeight: 600, color: C.t2 }}>{menuFmtNum(item.qty)}</span></span>
                <span style={{ fontSize: 9, color: C.t3 }}>PMIX <span style={{ fontWeight: 600, color: C.t2 }}>{item.pmix.toFixed(1)}%</span></span>
                {item.addonRev > 0 && <span style={{ fontSize: 9, color: C.t3 }}>Add-On <span style={{ fontWeight: 600, color: C.accent }}>{menuFmtFull(item.addonRev)}</span></span>}
                {item.refundQty > 0 && <span style={{ fontSize: 9, color: C.t3 }}>Ref <span style={{ fontWeight: 600, color: C.red }}>{item.refundQty}</span></span>}
              </div>
            </div>
          ))}
        </div>

        <div style={{ height: 80 }} />
      </div>

      <BottomNav active={1} />

      {/* Item Detail Sheet */}
      {selectedItem && (
        <div style={{ position: "absolute", inset: 0, zIndex: 100, display: "flex", flexDirection: "column" }}>
          <div style={{ flex: 1, background: "rgba(0,0,0,0.35)" }} onClick={() => setSelectedItem(null)} />
          <div style={{ background: C.surface, borderRadius: "18px 18px 0 0", maxHeight: "75%", overflowY: "auto", boxShadow: "0 -4px 24px rgba(0,0,0,0.12)" }}>
            <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 4px" }}>
              <div style={{ width: 36, height: 4, borderRadius: 2, background: C.divider }} />
            </div>
            <div style={{ padding: "4px 20px 12px" }}>
              <div style={{ fontSize: 10, color: C.t3, marginBottom: 3 }}>Item Detail</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: C.t1 }}>{selectedItem.name}</div>
            </div>
            <div style={{ padding: "0 16px 24px", display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                {[
                  { l: "Revenue", v: menuFmtFull(selectedItem.revenue) },
                  { l: "Qty Sold", v: menuFmtNum(selectedItem.qty) },
                  { l: "PMIX", v: `${(menuPmixData.find(p => p.name === selectedItem.name)?.pct || 0).toFixed(1)}%` },
                ].map((k, i) => (
                  <div key={i} style={{ background: C.bg, borderRadius: 10, padding: "10px 8px", textAlign: "center" }}>
                    <div style={{ fontSize: 7, color: C.t3, textTransform: "uppercase", letterSpacing: 0.3, marginBottom: 5 }}>{k.l}</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: C.t1 }}>{k.v}</div>
                  </div>
                ))}
              </div>
              {(() => { const ch = menuChannelItems.find(c => c.name === selectedItem.name); if (!ch) return null; const tot = ch.dinein+ch.delivery+ch.doordash+ch.ubereats; return (
                <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: 12, boxShadow: C.shadow }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: C.t1, marginBottom: 8 }}>Channel Split</div>
                  {[{l:"Dine-In",v:ch.dinein,c:menuChColors.dinein},{l:"Delivery",v:ch.delivery,c:menuChColors.delivery},{l:"DoorDash",v:ch.doordash,c:menuChColors.doordash},{l:"UberEats",v:ch.ubereats,c:menuChColors.ubereats}].map((x,i) => (
                    <div key={i} style={{ marginBottom: 6 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, marginBottom: 3 }}>
                        <span style={{ color: C.t2 }}>{x.l}</span>
                        <span style={{ fontWeight: 600, color: C.t1 }}>{menuFmtFull(x.v)} ({((x.v/tot)*100).toFixed(0)}%)</span>
                      </div>
                      <div style={{ background: C.hover, borderRadius: 3, height: 5 }}>
                        <div style={{ width: `${(x.v/tot)*100}%`, height: "100%", background: x.c, borderRadius: 3, opacity: 0.7 }} />
                      </div>
                    </div>
                  ))}
                </div>
              ); })()}
              {(() => { const a = menuAddonData.find(x => x.name === selectedItem.name); if (!a) return null; return (
                <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: 12, boxShadow: C.shadow }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: C.t1, marginBottom: 8 }}>Add-On Performance</div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: C.t2, marginBottom: 4 }}><span>Revenue</span><span style={{ fontWeight: 600, color: C.t1 }}>{menuFmtFull(a.addon)}</span></div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: C.t2 }}><span>Attach Rate</span><span style={{ fontWeight: 600, color: C.accent }}>{a.rate}%</span></div>
                </div>
              ); })()}
              {(() => { const r = menuRefundData.find(x => x.name === selectedItem.name); return r ? (
                <div style={{ background: C.card, border: `1px solid ${C.red}22`, borderRadius: 12, padding: 12, boxShadow: C.shadow }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: C.red, marginBottom: 8 }}>Refund Activity</div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: C.t2, marginBottom: 4 }}><span>Rate</span><span style={{ fontWeight: 600, color: C.red }}>{r.rate}%</span></div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: C.t2, marginBottom: 4 }}><span>Qty</span><span style={{ fontWeight: 600, color: C.t1 }}>{r.qty}</span></div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: C.t2 }}><span>Top Reason</span><span style={{ fontWeight: 500, color: C.orange }}>{r.reason}</span></div>
                </div>
              ) : (
                <div style={{ background: C.greenSoft, border: `1px solid rgba(22,163,74,0.12)`, borderRadius: 12, padding: 12 }}>
                  <div style={{ fontSize: 11, color: C.green, fontWeight: 500 }}>No refunds recorded</div>
                </div>
              ); })()}
            </div>
          </div>
        </div>
      )}
    </Phone>
  );
};

// =========== MAIN ===========
export default function MobileViews() {
  return (
    <div style={{ minHeight: "100vh", background: "#E8EAF0", padding: "40px 20px", fontFamily: "'DM Sans', -apple-system, sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
      <link href="https://fonts.googleapis.com/css2?family=Material+Symbols+Rounded:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200" rel="stylesheet" />
      <style>{`
        .material-symbols-rounded {
          font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
      `}</style>

      <div style={{ textAlign: "center", marginBottom: 40 }}>
        <div style={{ fontSize: 28, fontWeight: 700, color: "#1A1D2E", letterSpacing: -0.5 }}>Maghil Analytics — Mobile (v6)</div>
        <div style={{ fontSize: 14, color: "#5A6178", marginTop: 6 }}>Brand theme #D23A3B · Light · Material Symbols Rounded</div>
      </div>
      <div style={{ textAlign: "center", marginBottom: 20 }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: "#1A1D2E" }}>Main Screens</div>
      </div>
      <div style={{ display: "flex", gap: 28, justifyContent: "center", flexWrap: "wrap", alignItems: "flex-start", marginBottom: 60 }}>
        <SnapshotScreen />
        <ReportsScreen />
        <AIScreen />
        <SupportScreen />
      </div>
      <div style={{ textAlign: "center", marginBottom: 20 }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: "#1A1D2E" }}>Snapshot Drill-In Screens</div>
        <div style={{ fontSize: 12, color: "#5A6178", marginTop: 4 }}>Tap any KPI with › arrow for detailed breakdown</div>
      </div>
      <div style={{ display: "flex", gap: 28, justifyContent: "center", flexWrap: "wrap", alignItems: "flex-start" }}>
        <DrillNetSales />
        <DrillOrders />
        <DrillCancelled />
        <DrillStaff />
        <DrillVoids />
        <DrillTips />
      </div>
      <div style={{ textAlign: "center", marginTop: 60, marginBottom: 20 }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: "#1A1D2E" }}>Report Drill-In Screens</div>
        <div style={{ fontSize: 12, color: "#5A6178", marginTop: 4 }}>Tap "Sales" on Reports page for the full Sales Report · Tap "Product" for Menu Performance</div>
      </div>
      <div style={{ display: "flex", gap: 28, justifyContent: "center", flexWrap: "wrap", alignItems: "flex-start" }}>
        <ProductMenuScreen />
      </div>
    </div>
  );
}
